/**
 * A screen that belongs to a module, refused when the business has it off.
 *
 * The sidebar already hides the entry; this covers the address bar, a
 * bookmark and a link from somewhere else. The API refuses regardless — this
 * is so the person sees why, and where to turn it on, instead of a screen of
 * failed requests.
 *
 * When the module list cannot be read the screen is let through and the API
 * decides, as the sidebar does: a menu that cannot ask must not lock somebody
 * out of their own tool.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MODULE_LABELS, type ModuleKey } from '@cenox/shared';
import { useAuth } from '../lib/auth';
import { useModules } from '../lib/modules';
import { Card, Spinner } from './ui';

export function RequireModule({ module, children }: { module: ModuleKey; children: ReactNode }): ReactNode {
  const { active, known, isLoading } = useModules();
  const { can } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (!known || active.has(module)) return <>{children}</>;

  const label = MODULE_LABELS[module];
  return (
    <Card className="mx-auto mt-6 max-w-lg">
      <h1 className="text-base font-semibold text-slate-900">{label} is switched off for this business</h1>
      <p className="mt-1 text-sm text-slate-600">
        Nothing has been deleted. Switching it on brings back everything that was recorded.
      </p>
      {can('settings:update') ? (
        <Link
          to="/settings?tab=modules"
          className="mt-4 inline-block rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
        >
          Turn it on in Settings
        </Link>
      ) : (
        <p className="mt-3 text-sm text-slate-500">Ask the owner or an admin if you need it.</p>
      )}
    </Card>
  );
}
